import { supabase } from '@/lib/supabase';
import { unwrap, ServiceError } from '@/data/serviceError';
import { compressImage } from '@/lib/imageCompression';
import { getSignedUrls } from '@/lib/storageUrl';
import type { Tables } from '@/types/db';
import { MAX_FEEDBACK_FOTOS, type Feedback } from '../lib/feedbackRegels';

/** Eigen melding waarop de melder om feedback gevraagd wordt (of al gaf). */
export type EigenMelding = Pick<
  Tables<'software_meldingen'>,
  | 'id'
  | 'melder_id'
  | 'soort'
  | 'omschrijving'
  | 'scherm'
  | 'status'
  | 'beoordeling'
  | 'uitleg'
  | 'beoordeeld_op'
  | 'feedback'
  | 'feedback_opmerking'
  | 'feedback_foto_paths'
  | 'feedback_op'
  | 'created_at'
>;

const EIGEN_KOLOMMEN =
  'id, melder_id, soort, omschrijving, scherm, status, beoordeling, uitleg, beoordeeld_op, feedback, feedback_opmerking, feedback_foto_paths, feedback_op, created_at';

/** Bucket en map van de feedbackfoto's: dezelfde map als de screenshot van de melding. */
const FOTO_BUCKET = 'documents';

/**
 * Feedback van de melder op zijn softwaremelding (besluit Tom 01-10-2026).
 * Het schrijven loopt via de RPC melding_feedback_geven; de database bewaakt
 * wie wat mag (eigen melding, status opgelost/afgewezen, één keer, max 5 foto's).
 */
export const meldingFeedbackService = {
  /** Mijn beoordeelde meldingen zonder feedback: de vraag "werkt het nu?". */
  async feedbackGevraagd(userId: string): Promise<EigenMelding[]> {
    return unwrap(
      await supabase
        .from('software_meldingen')
        .select(EIGEN_KOLOMMEN)
        .eq('melder_id', userId)
        .in('status', ['opgelost', 'afgewezen'])
        .is('feedback', null)
        .order('beoordeeld_op', { ascending: false, nullsFirst: false }),
      'Feedbackvragen laden',
    );
  },

  /**
   * Foto's bij de feedback uploaden (gecomprimeerd) naar
   * documents/meldingen/<uid>/<ts>-feedback-<n>.jpg. Geeft de paden terug.
   */
  async uploadFotos(userId: string, fotos: File[]): Promise<string[]> {
    if (fotos.length > MAX_FEEDBACK_FOTOS) {
      throw new ServiceError(`Maximaal ${MAX_FEEDBACK_FOTOS} foto's bij feedback`, '22023');
    }
    const ts = Date.now();
    const paden: string[] = [];
    for (const [i, foto] of fotos.entries()) {
      const klein = await compressImage(foto);
      const pad = `meldingen/${userId}/${ts}-feedback-${i + 1}.jpg`;
      const { error } = await supabase.storage
        .from(FOTO_BUCKET)
        .upload(pad, klein, { contentType: klein.type || 'image/jpeg', upsert: true });
      if (error) throw new ServiceError(`Foto ${i + 1} uploaden mislukt: ${error.message}`);
      paden.push(pad);
    }
    return paden;
  },

  async geefFeedback(
    meldingId: string,
    feedback: Feedback,
    opmerking: string,
    fotoPaden: string[] = [],
  ): Promise<void> {
    const { error } = await supabase.rpc('melding_feedback_geven', {
      p_melding_id: meldingId,
      p_feedback: feedback,
      p_opmerking: opmerking.trim() || null,
      p_foto_paths: fotoPaden,
    });
    if (error) throw new ServiceError(error.message, error.code);
  },

  /** Tijdelijke links naar de feedbackfoto's (voor Tom en de meekijkers). */
  async fotoLinks(paden: string[]): Promise<Record<string, string>> {
    return getSignedUrls(FOTO_BUCKET, paden);
  },
};
